import { type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { IconButton } from "../../actions/IconButton";

export type WorkspaceHeaderProps = {
  /** Brand block (logo + product name) rendered at the far left. */
  brand?: ReactNode;
  /** Fixed width (px) for the brand rail, e.g. to align with a sidebar. Defaults to 216. */
  brandWidth?: number;
  /** Collapses the brand rail to the sidebar-toggle column and hides the brand slot. */
  brandCollapsed?: boolean;
  /** Fixed width (px) for the collapsed brand rail. Defaults to 56. */
  collapsedBrandWidth?: number;
  /** When provided, renders a sidebar-toggle button next to the brand. */
  onToggleSidebar?: () => void;
  /** Accessible label for the sidebar-toggle button. */
  toggleLabel?: string;
  /** Optional tab strip, rendered as its own bordered column after the brand. */
  tabs?: ReactNode;
  /** Fixed width (px) for the tabs column, e.g. to align with a secondary pane. Defaults to 380. */
  tabsWidth?: number;
  /** Collapses the tabs rail to a compact centered column. */
  tabsCollapsed?: boolean;
  /** Fixed width (px) for the collapsed tabs rail. Defaults to 40. */
  collapsedTabsWidth?: number;
  /** Search control, placed at the start of the trailing actions area. */
  search?: ReactNode;
  /** Fixed width (px) for the search slot wrapper. Defaults to 300. */
  searchWidth?: number;
  /** Trailing action controls (wallet, apps, avatar, …). */
  actions?: ReactNode;
  /** Free-form content placed in the main (flex) region before the spacer. */
  children?: ReactNode;
  /** Header height in px. Defaults to 52. */
  height?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Application top bar for workspace-style layouts. Slot-based: drop in a brand
 * block, an optional sidebar toggle, a tab strip, a search control, and trailing
 * actions. All product-specific logic (which tabs exist, wallet balance, etc.)
 * stays in the consuming app — this component only owns layout and chrome.
 */
export function WorkspaceHeader({
  brand,
  brandWidth = 216,
  brandCollapsed = false,
  collapsedBrandWidth = 56,
  onToggleSidebar,
  toggleLabel = "Toggle sidebar",
  tabs,
  tabsWidth = 380,
  tabsCollapsed = false,
  collapsedTabsWidth = 40,
  search,
  searchWidth = 280,
  actions,
  children,
  height = 52,
  className,
  style
}: WorkspaceHeaderProps) {
  const resolvedBrandWidth = brandCollapsed ? collapsedBrandWidth : brandWidth;
  const resolvedTabsWidth = tabsCollapsed ? collapsedTabsWidth : tabsWidth;

  return (
    <header
      className={cn(
        "bfrs-relative bfrs-z-20 bfrs-flex bfrs-shrink-0 bfrs-items-stretch bfrs-border-b bfrs-border-border-strong bfrs-bg-surface-card",
        className
      )}
      style={{ height, ...style }}
    >
      {(brand || onToggleSidebar) && (
        <div
          className={cn(
            "bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-2 bfrs-border-r bfrs-border-border-strong bfrs-transition-[width,min-width] bfrs-duration-[220ms] bfrs-ease-out",
            brandCollapsed ? "bfrs-justify-center bfrs-px-0" : "bfrs-justify-start bfrs-pl-4 bfrs-pr-[11px]"
          )}
          style={{ width: resolvedBrandWidth, minWidth: resolvedBrandWidth }}
        >
          {!brandCollapsed && brand}
          {onToggleSidebar && (
            <IconButton
              aria-label={toggleLabel}
              title={toggleLabel}
              variant="secondary"
              size="sm"
              className={cn(
                "bfrs-size-[26px] bfrs-rounded-[6px] bfrs-bg-surface-card bfrs-text-text-muted hover:bfrs-bg-primary-soft",
                !brandCollapsed && "bfrs-ml-auto"
              )}
              icon={
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                  <rect x="1" y="1" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M5 1v11" stroke="currentColor" strokeWidth="1.3" />
                </svg>
              }
              onClick={onToggleSidebar}
            />
          )}
        </div>
      )}

      {tabs && (
        <div
          className={cn(
            "bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-2 bfrs-overflow-x-hidden bfrs-border-r bfrs-border-border-strong bfrs-bg-surface-card bfrs-transition-[width,min-width] bfrs-duration-[220ms] bfrs-ease-out bfrs-[scrollbar-width:none]",
            tabsCollapsed ? "bfrs-justify-center bfrs-px-0" : "bfrs-justify-start bfrs-pl-3.5 bfrs-pr-2"
          )}
          style={{ width: resolvedTabsWidth, minWidth: resolvedTabsWidth }}
        >
          {tabs}
        </div>
      )}

      <div className="bfrs-flex bfrs-flex-1 bfrs-items-center bfrs-gap-2 bfrs-py-0 bfrs-pl-5 bfrs-pr-3">
        {children}
        {search && (
          <div className="bfrs-flex bfrs-shrink-0 bfrs-items-center" style={{ width: searchWidth }}>
            {search}
          </div>
        )}
        <div className="bfrs-flex-1" />
        {actions}
      </div>
    </header>
  );
}

WorkspaceHeader.displayName = "WorkspaceHeader";
